'use client';

import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

/**
 * Button — primary action component.
 *
 * Variants tuned for both surfaces:
 *   - primary    — brand-filled, the dominant CTA
 *   - secondary  — sand-tinted, supporting action
 *   - outline    — bordered, low-emphasis
 *   - ghost      — chromeless, in-table actions, nav links
 *   - destructive— error-tinted, destructive actions only
 *   - link       — text-only, inline link
 *
 * Sizes:
 *   - sm  (32px)  — inline / table cell actions
 *   - md  (40px)  — default
 *   - lg  (48px)  — hero CTAs (marketing)
 *   - icon (40px) — square icon-only button
 *
 * RTL: paddings use logical properties via Tailwind's ms-/me- utilities.
 *      Icon-only buttons are direction-agnostic. Loading spinner replaces
 *      content; disabled state preserves outline for visual stability.
 *
 * a11y:
 *   - Focus ring via :focus-visible (token --shadow-focus)
 *   - Loading state sets aria-busy and disables interaction
 *   - When asChild, all a11y attributes pass through to the rendered child
 */

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md font-medium select-none',
    'transition-colors duration-fast ease-out',
    'focus-visible:outline-none',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-busy:opacity-70 aria-busy:cursor-wait',
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'bg-brand-500 text-text-onBrand shadow-sm',
          'hover:bg-brand-600 active:bg-brand-700',
        ),
        secondary: cn(
          'bg-sand-100 text-text-primary',
          'hover:bg-sand-200 active:bg-sand-300',
          'border border-border-subtle',
        ),
        outline: cn(
          'border border-border-default bg-transparent text-text-primary',
          'hover:bg-sand-100 active:bg-sand-200',
        ),
        ghost: cn(
          'bg-transparent text-text-primary',
          'hover:bg-sand-100 active:bg-sand-200',
        ),
        destructive: cn(
          'bg-error-500 text-white shadow-sm',
          'hover:bg-error-600 active:bg-error-700',
        ),
        link: cn(
          'text-brand-600 underline-offset-4',
          'hover:text-brand-700 hover:underline',
          'h-auto px-0 py-0',
        ),
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild, isLoading, startIcon, endIcon, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const disabled = props.disabled || isLoading;
    // Spinner replaces content only for a real <button>. In the asChild (Slot)
    // path the loading state isn't used (links don't load) and a Slottable must
    // always be present so Slot can find the real child.
    const showSpinner = isLoading && !asChild;

    // IMPORTANT: the icon spans and <Slottable> must be DIRECT children of Comp,
    // NOT wrapped in a fragment. Radix Slot only descends one level
    // (React.Children.toArray) to find the <Slottable>; a wrapping fragment hides
    // it, and Slot then clones the fragment — which silently drops className,
    // variant, size, ref, data-testid and every other prop onto a no-op Fragment
    // (the bug that made every <Button asChild> render an unstyled child). With
    // <Slottable> as a direct child, Slot merges all props/ref onto the real
    // element (the <a>/<Link>) and keeps the icon spans as its siblings.
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        aria-busy={isLoading || undefined}
        disabled={disabled}
        {...props}
      >
        {showSpinner ? <Spinner /> : null}
        {!showSpinner && startIcon ? (
          <span className="inline-flex shrink-0">{startIcon}</span>
        ) : null}
        {!showSpinner ? <Slottable>{children}</Slottable> : null}
        {!showSpinner && endIcon ? (
          <span className="inline-flex shrink-0">{endIcon}</span>
        ) : null}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export { buttonVariants };
